import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { 
  FaCheckCircle, 
  FaUsers, 
  FaLightbulb, 
  FaRocket, 
  FaLinkedin,
  FaGithub,
  FaHandshake
} from 'react-icons/fa';
import { 
  Card, 
  CardBody, 
  Progress, 
  Tabs, 
  Tab, 
  Avatar
} from "@heroui/react";

const About = () => {
  // Animation controls
  const controls = useAnimation();
  const statsControls = useAnimation();
  
  // Intersection observer for animation triggers
  const aboutRef = useRef(null);
  const statsRef = useRef(null);
  const isAboutInView = useInView(aboutRef, { once: true, amount: 0.2 });
  const isStatsInView = useInView(statsRef, { once: true, amount: 0.3 });
  
  // Active tab state
  const [activeTab, setActiveTab] = useState("company");
  
  // Counter animation state
  const [counters, setCounters] = useState({
    years: 7,
    clients: 50,
    projects: 40,
    availability: 90
  });
  
  // Animate elements when they come into view
  useEffect(() => {
    if (isAboutInView) {
      controls.start("visible");
    }
    
    if (isStatsInView) {
      statsControls.start("visible");
      
      // Animate counters
      const duration = 2000; // 2 seconds
      const frameRate = 30; // frames per second
      const totalFrames = duration / 1000 * frameRate;
      
      let frame = 0;
      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        
        setCounters({
          years: Math.floor(progress * 7),
          clients: Math.floor(progress * 50),
          projects: Math.floor(progress * 40),
          availability: 90 + (progress * 9.99)
        });
        
        if (frame === totalFrames) {
          clearInterval(timer);
        }
      }, 1000 / frameRate);
      
      return () => clearInterval(timer);
    }
  }, [isAboutInView, isStatsInView, controls, statsControls]);

  // Team members data - tylko 2 osoby
  const teamMembers = [
    {
      id: 1,
      name: 'Jakub Kupidura',
      position: 'CEO & Administrator IT',
      bio: 'Ekspert ds. obsługi klienta oraz doświadczony specjalista we wdrażaniu i optymalizacji nowoczesnych rozwiązań technologicznych',
      image: require('./assets/img/jk.jpg'),
      social: {
        linkedin: '#',
        github: '#'
      },
      skills: [
        { name: 'Wsparcie techniczne', level: 99 },
        { name: 'Analityka danych', level: 99 },
        { name: 'Infrastruktura IT', level: 99 },
        { name: 'Bezpieczeństwo IT', level: 95 }
      ]
    },
    {
      id: 2,
      name: 'Bartłomiej Stawarz',
      position: 'CEO  & DevOps',
      bio: 'Ekspert w dziedzinie infrastruktury i bezpieczeństwa IT. Pasjonat nowych technologii i rozwiązywania problemów.',
      image: require('./assets/img/bs.JPG'),
      social: {
        linkedin: '#',
        github: '#'
      },
      skills: [
        { name: 'Infrastruktura IT', level: 99 },
        { name: 'Bezpieczeństwo IT', level: 99 },
        { name: 'Inżynieria danych & AI', level: 95 },
        { name: 'Zarządzanie projektami i procesami IT', level: 95 }
      ]
    }
  ];

  // Core values data
  const coreValues = [
    {
      icon: <FaLightbulb className="text-warning" size={36} />,
      title: 'Prostota',
      description: 'Wierzymy, że technologia powinna być prosta i zrozumiała. Tłumaczymy skomplikowane zagadnienia w przystępny sposób.'
    },
    {
      icon: <FaHandshake className="text-primary" size={36} />,
      title: 'Partnerstwo',
      description: 'Budujemy długotrwałe relacje oparte na zaufaniu i wzajemnych korzyściach. Jesteśmy partnerem, nie tylko dostawcą usług.'
    },
    {
      icon: <FaUsers className="text-success" size={36} />,
      title: 'Dostępność',
      description: 'Technologia powinna być dostępna dla wszystkich, niezależnie od wielkości firmy czy budżetu. Oferujemy rozwiązania dopasowane do potrzeb.'
    },
    {
      icon: <FaRocket className="text-secondary" size={36} />,
      title: 'Rozwój',
      description: 'Nieustannie się rozwijamy, aby dostarczać najnowsze i najlepsze rozwiązania, które pomogą Twojej firmie rosnąć.'
    }
  ];

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 20 }
    }
  };

  // Stats counters animation
  const statsVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div id="about" className="py-20 bg-default-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 bg-primary rounded-full filter blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        />
        <motion.div
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-secondary rounded-full filter blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            repeatType: 'reverse'
          }}
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={aboutRef}>
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-primary font-medium text-sm tracking-wider uppercase mb-2 inline-block">Poznaj nas</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            O <span className="text-primary">Naszej Firmie</span>
          </h2>
          <p className="text-foreground-muted max-w-2xl mx-auto">
            IT For Everyone to firma stworzona z myślą o dostarczaniu przyjaznych i zrozumiałych rozwiązań IT 
            dla firm każdej wielkości. Nasza misja to sprawić, by technologia była dostępna dla wszystkich.
          </p>
        </motion.div>

        {/* Tabs for different about sections */}
        <div className="mb-12">
          <Tabs 
            aria-label="O Firmie"
            selectedKey={activeTab}
            onSelectionChange={setActiveTab}
            color="primary"
            variant="underlined"
            classNames={{
              base: "flex justify-center",
              tabList: "gap-4 md:gap-8",
              cursor: "bg-primary",
              tab: "max-w-fit px-2 h-10 data-[selected=true]:font-semibold"
            }}
          >
            <Tab key="company" title="O firmie" />
            <Tab key="team" title="Zespół" />
            <Tab key="values" title="Nasze wartości" />
          </Tabs>
        </div>

        {/* Tab content with smooth transitions */}
        <div className="min-h-[500px]">
          {activeTab === "company" && (
              <div className="mb-16 max-w-3xl mx-auto">
                {/* Company description */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <motion.div variants={itemVariants}>
                    <h3 className="text-2xl font-bold mb-6">
                      Twój zaufany partner w świecie technologii
                    </h3>
                    <p className="text-foreground-muted mb-6">
                      IT For Everyone powstało z przekonania, że technologia powinna być dostępna dla każdego.
                      Jesteśmy dynamicznie rozwijającą się firmą, która specjalizuje się w dostarczaniu 
                      przejrzystych i skutecznych rozwiązań IT dla małych, średnich i dużych przedsiębiorstw.
                    </p>
                    <p className="text-foreground-muted mb-6">
                      Naszą misją jest upraszczanie technologii i sprawienie, by służyła Twojemu biznesowi,
                      zamiast być przeszkodą w jego rozwoju. Wierzymy, że każda firma, niezależnie od wielkości,
                      zasługuje na profesjonalne wsparcie IT.
                    </p>
                    <p className="text-foreground-muted mb-8">
                      Założyliśmy IT For Everyone, aby pomagać firmom, które potrzebują specjalisty lub nie mają własnych działów IT, a jednocześnie 
                      potrzebują solidnego wsparcia technicznego i prostych rozwiązań, które pomogą im się rozwijać.
                      Dzięki naszemu doświadczeniu i podejściu zorientowanemu na klienta, możemy zapewnić usługi IT, które
                      są jednocześnie profesjonalne i przyjazne.
                    </p>

                    {/* Company benefits/advantages */}
                    <ul className="space-y-4">
                      <motion.li 
                        variants={itemVariants}
                        className="flex items-start"
                      >
                        <span className="bg-primary/10 p-2 rounded-full mr-4">
                          <FaCheckCircle className="text-primary" />
                        </span>
                        <div>
                          <h4 className="font-medium mb-1">Zrozumiały język</h4>
                          <p className="text-sm text-foreground-muted">Tłumaczymy skomplikowane zagadnienia IT w przystępny sposób.</p>
                        </div>
                      </motion.li>
                      <motion.li 
                        variants={itemVariants}
                        className="flex items-start"
                      >
                        <span className="bg-primary/10 p-2 rounded-full mr-4">
                          <FaCheckCircle className="text-primary" />
                        </span>
                        <div>
                          <h4 className="font-medium mb-1">Indywidualne podejście</h4>
                          <p className="text-sm text-foreground-muted">Każdy projekt traktujemy wyjątkowo, dostosowując rozwiązania do Twoich potrzeb.</p>
                        </div>
                      </motion.li>
                      <motion.li 
                        variants={itemVariants}
                        className="flex items-start"
                      >
                        <span className="bg-primary/10 p-2 rounded-full mr-4">
                          <FaCheckCircle className="text-primary" />
                        </span>
                        <div>
                          <h4 className="font-medium mb-1">Przejrzyste zasady</h4>
                          <p className="text-sm text-foreground-muted">Jasno komunikujemy koszty, terminy i zakres prac - bez ukrytych opłat.</p>
                        </div>
                      </motion.li>
                      <motion.li 
                        variants={itemVariants}
                        className="flex items-start"
                      >
                        <span className="bg-primary/10 p-2 rounded-full mr-4">
                          <FaCheckCircle className="text-primary" />
                        </span>
                        <div>
                          <h4 className="font-medium mb-1">Wsparcie na każdym etapie</h4>
                          <p className="text-sm text-foreground-muted">Zapewniamy stałe wsparcie techniczne i pomoc w rozwijaniu Twojego biznesu.</p>
                        </div>
                      </motion.li>
                    </ul>
                  </motion.div>
                </motion.div>
              </div>
          )}

          {activeTab === "team" && (
            <div>
              {/* Team introduction */}
              <div className="mb-12 text-center">
                <h3 className="text-2xl font-bold mb-4">Poznaj naszych założycieli</h3>
                <p className="text-foreground-muted max-w-2xl mx-auto">
                  Za sukcesem IT For Everyone stoją ludzie - zespół pasjonatów technologii,
                  którzy wierzą, że IT może być proste, przystępne i przydatne dla każdego biznesu ale wszystko zaczęło się od ich pomysłu. 
                </p>
              </div>
              
              {/* Team members grid - tylko 2 osoby w układzie 1-1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
                {teamMembers.map((member, index) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2, duration: 0.5 }}
                    whileHover={{ y: -10, transition: { duration: 0.2 } }}
                  >
                    <Card className="shadow-lg overflow-hidden">
                      <CardBody className="p-0">
                        <div className="relative overflow-hidden group">
                          <div className="w-full aspect-square bg-default-100 relative overflow-hidden">
                            <Avatar
                              src={member.image}
                              className="w-full h-full text-large"
                              isBordered
                              color="primary"
                              classNames={{
                                base: "w-full h-full",
                                img: "object-cover"
                              }}
                            />
                            
                            {/* Overlay with social links */}
                            <motion.div 
                              className="absolute inset-0 bg-primary/70 backdrop-blur-sm flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                              whileHover={{ opacity: 1 }}
                            >
                              <h4 className="text-white font-semibold mb-1">{member.name}</h4>
                              <p className="text-white/90 text-sm mb-4">{member.position}</p>
                              
                              <div className="flex gap-3">
                                <motion.a 
                                  href={member.social.linkedin} 
                                  className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-primary"
                                  whileHover={{ y: -3, scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaLinkedin />
                                </motion.a>
                                <motion.a 
                                  href={member.social.github} 
                                  className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-primary"
                                  whileHover={{ y: -3, scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <FaGithub />
                                </motion.a>
                              </div>
                            </motion.div>
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <h4 className="text-lg font-semibold">{member.name}</h4>
                          <p className="text-primary text-sm mb-3">{member.position}</p>
                          <p className="text-foreground-muted text-sm mb-4">{member.bio}</p>
                          
                          {/* Skills */}
                          <div className="space-y-2">
                            {member.skills.map((skill, idx) => (
                              <div key={idx} className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span>{skill.name}</span>
                                  <span className="text-primary font-medium">{skill.level}%</span>
                                </div>
                                <Progress 
                                  value={skill.level} 
                                  color="primary" 
                                  size="sm" 
                                  className="h-1"
                                  aria-label={`${skill.name} - ${skill.level}%`}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "values" && (
            <div>
              {/* Values introduction */}
              <div className="mb-12 text-center">
                <h3 className="text-2xl font-bold mb-4">Nasze podstawowe wartości</h3>
                <p className="text-foreground-muted max-w-2xl mx-auto">
                  Kierujemy się jasno określonymi wartościami, które stanowią fundament naszej pracy,
                  relacji z klientami i rozwoju firmy.
                </p>
              </div>
              
              {/* Core values grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {coreValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.4 }}
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  >
                    <Card className="shadow-md text-center h-full">
                      <CardBody className="flex flex-col items-center">
                        <motion.div 
                          className="mb-6 mt-4"
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 3, repeat: Infinity, delay: index * 1.5 }}
                        >
                          {value.icon}
                        </motion.div>
                        <h4 className="text-xl font-semibold mb-3">{value.title}</h4>
                        <p className="text-foreground-muted">{value.description}</p>
                      </CardBody>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Company approach */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-center">Nasze podejście</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <Card className="shadow-md border-t-4 border-primary h-full">
                      <CardBody>
                        <h4 className="text-lg font-semibold mb-4">Dla klientów</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <FaCheckCircle className="text-primary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Partnerskie podejście i transparentność</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-primary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Terminowość i dotrzymywanie zobowiązań</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-primary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Elastyczność i dostosowanie do potrzeb</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-primary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Jasna i prosta komunikacja</span>
                          </li>
                        </ul>
                      </CardBody>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Card className="shadow-md border-t-4 border-success h-full">
                      <CardBody>
                        <h4 className="text-lg font-semibold mb-4">Dla zespołu</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <FaCheckCircle className="text-success mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Ciągłe doskonalenie umiejętności</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-success mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Work-life balance i elastyczność</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-success mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Otwarta komunikacja i wzajemne wsparcie</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-success mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Pasja do nowych technologii</span>
                          </li>
                        </ul>
                      </CardBody>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Card className="shadow-md border-t-4 border-secondary h-full">
                      <CardBody>
                        <h4 className="text-lg font-semibold mb-4">Dla społeczności</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <FaCheckCircle className="text-secondary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Edukacja technologiczna małych firm</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-secondary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Promowanie dobrych praktyk IT</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-secondary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Odpowiedzialne korzystanie z technologii</span>
                          </li>
                          <li className="flex items-start">
                            <FaCheckCircle className="text-secondary mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">Stosowanie etycznych praktyk biznesowych</span>
                          </li>
                        </ul>
                      </CardBody>
                    </Card>
                  </motion.div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics section - zredukowane wartości */}
        <motion.div 
          ref={statsRef}
          variants={statsVariants}
          initial="hidden"
          animate={statsControls}
          className="mt-20"
        >
          <Card className="shadow-xl" isBlurred>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {[
                  {
                    value: counters.years,
                    suffix: "+",
                    label: "Lat Doświadczenia",
                    color: "primary"
                  },
                  {
                    value: counters.clients,
                    suffix: "+",
                    label: "Zadowolonych Klientów",
                    color: "success"
                  },
                  {
                    value: counters.projects,
                    suffix: "+",
                    label: "Zrealizowanych Projektów",
                    color: "secondary"
                  },
                  {
                    value: counters.availability.toFixed(2),
                    suffix: "%",
                    label: "Dostępności Wsparcia",
                    color: "warning"
                  }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    className="text-center"
                  >
                    <motion.div 
                      className={`text-3xl md:text-4xl font-bold text-${stat.color} mb-2`}
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse', delay: index * 0.5 }}
                    >
                      {stat.value}{stat.suffix}
                    </motion.div>
                    <p className="text-foreground-muted mb-2">
                      {stat.label}
                    </p>
                    <Progress 
                      value={parseInt(stat.value)} 
                      maxValue={stat.label.includes("Dostępności") ? 100 : stat.label.includes("Lata") ? 10 : 100}
                      color={stat.color} 
                      size="sm" 
                      showValueLabel={false}
                      aria-label="Progress"
                      className="mt-2"
                    />
                  </motion.div>
                ))}
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
